/**
 * Client-side paise math for the PO composer preview.
 *
 * Reproduces the domain's deterministic computation so the preview totals
 * match what the server will recompute from the same operator strings:
 *
 *   qtyMilli   = qty × 1000           (3-decimal quantity)
 *   ratePaise  = rupees × 100
 *   discountBps / taxRateBps = pct × 100   (2-decimal percent → basis points)
 *   gross      = ratePaise × qtyMilli / 1000
 *   discount   = gross × discountBps / 10000
 *   taxable    = gross − discount
 *   tax        = taxable × taxRateBps / 10000
 *
 * Subtotal = Σ taxable, tax = Σ tax, total = subtotal + tax. All bigint —
 * never floats (AGENTS.md §7 rule 7).
 *
 * @see packages/api-contract/src/procedures/purchase-orders.ts (poLineInput)
 * @see apps/web/src/features/bills/new/bill-math.ts (the bill equivalent)
 */
import { rupeesToPaise } from "../../../lib/format.js";
import type { PoLineItem } from "./form-schema.js";

export interface PoTotals {
  subtotalPaise: bigint;
  taxPaise: bigint;
  totalPaise: bigint;
}

/** Taxable amount for one line (gross − discount), in paise. */
export function computeLineTaxablePaise(row: PoLineItem): bigint {
  const qtyMilli = parseDecimalMillis(row.qty);
  const ratePaise = rupeesToPaise(row.rate);
  const discountBps = parsePercentBps(row.discountRate);
  if (qtyMilli === null || ratePaise === null || discountBps === null) return 0n;
  const gross = (ratePaise * qtyMilli) / 1000n;
  return gross - (gross * discountBps) / 10000n;
}

/** Tax for one line, in paise. */
export function computeLineTaxPaise(row: PoLineItem): bigint {
  const taxBps = parsePercentBps(row.taxRate);
  if (taxBps === null) return 0n;
  return (computeLineTaxablePaise(row) * taxBps) / 10000n;
}

/** Line total (taxable + tax), in paise — what the table's Amount column shows. */
export function computeLineTotalPaise(row: PoLineItem): bigint {
  return computeLineTaxablePaise(row) + computeLineTaxPaise(row);
}

export function computeTotals(rows: PoLineItem[]): PoTotals {
  return rows.reduce<PoTotals>(
    (acc, row) => {
      const taxable = computeLineTaxablePaise(row);
      const tax = computeLineTaxPaise(row);
      return {
        subtotalPaise: acc.subtotalPaise + taxable,
        taxPaise: acc.taxPaise + tax,
        totalPaise: acc.totalPaise + taxable + tax,
      };
    },
    { subtotalPaise: 0n, taxPaise: 0n, totalPaise: 0n },
  );
}

export function hasValidPoLine(row: PoLineItem): boolean {
  return row.description.trim() !== "" && computeLineTaxablePaise(row) > 0n;
}

function parseDecimalMillis(value: string): bigint | null {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,3})?$/.test(cleaned)) return null;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  const result = BigInt(whole || "0") * 1000n + BigInt((fraction + "000").slice(0, 3));
  return result > 0n ? result : null;
}

function parsePercentBps(value: string): bigint | null {
  const cleaned = value.trim();
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  const [whole = "0", fraction = ""] = cleaned.split(".");
  return BigInt(whole || "0") * 100n + BigInt((fraction + "00").slice(0, 2));
}
